// Shell of every job and lifecycle dialog: JobDialogFrame is the modal (an alertdialog that ignores outside clicks for destructive actions) whose content mounts only while open, and JobDialogLayout is the form with header, body, an optional typed confirmation, the problem of a failed attempt and Cancel / confirm.
import { useId, useState, type ReactNode } from "react";

import { ProblemState } from "@/components/common/ProblemState";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";

interface JobDialogFrameProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  destructive?: boolean;
  size?: "default" | "sm" | "lg";
  children: ReactNode;
}

export function JobDialogFrame({
  open,
  onOpenChange,
  destructive = false,
  size = "default",
  children,
}: JobDialogFrameProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size={size}
        role={destructive ? "alertdialog" : "dialog"}
        {...(destructive
          ? {
              onInteractOutside: (event: Event) => {
                event.preventDefault();
              },
            }
          : {})}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
}

interface JobDialogLayoutProps {
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  confirmLabel: string;
  confirmText?: string | undefined;
  canConfirm?: boolean;
  pending?: boolean;
  error?: unknown;
  note?: ReactNode;
  onConfirm: () => void;
}

export function JobDialogLayout({
  title,
  description,
  children,
  confirmLabel,
  confirmText,
  canConfirm = true,
  pending = false,
  error,
  note,
  onConfirm,
}: JobDialogLayoutProps) {
  const [typed, setTyped] = useState("");
  const inputId = useId();
  const hintId = useId();
  const confirmed = confirmText === undefined || typed.trim() === confirmText;
  const ready = canConfirm && confirmed && !pending;
  return (
    <form
      noValidate
      className="flex min-h-0 flex-col"
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) {
          onConfirm();
        }
      }}
    >
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        {description ? <DialogDescription>{description}</DialogDescription> : null}
      </DialogHeader>
      <DialogBody className="flex flex-col gap-4">
        {children}
        {confirmText === undefined ? null : (
          <div className="flex flex-col gap-[5px]">
            <Label htmlFor={inputId}>
              Type <span className="font-mono font-semibold text-foreground">{confirmText}</span> to confirm
            </Label>
            <Input
              id={inputId}
              value={typed}
              autoComplete="off"
              spellCheck={false}
              aria-describedby={hintId}
              aria-invalid={typed.length > 0 && !confirmed}
              onChange={(event) => {
                setTyped(event.target.value);
              }}
            />
            <p id={hintId} className="m-0 text-label text-muted-foreground">
              {confirmed ? "Confirmed." : "The action stays disabled until the text matches exactly."}
            </p>
          </div>
        )}
        {error ? <ProblemState problem={error} compact /> : null}
      </DialogBody>
      <DialogFooter>
        <div className="min-w-0 text-chip text-muted-foreground">{note}</div>
        <div className="flex shrink-0 items-center gap-2">
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={pending}>
              Cancel
            </Button>
          </DialogClose>
          <Button type="submit" disabled={!ready}>
            {pending ? <Spinner label="Working" /> : null}
            {confirmLabel}
          </Button>
        </div>
      </DialogFooter>
    </form>
  );
}
